/**
 * Airtable Service - evidence records via the server-side API
 *
 * All Airtable access happens in /api/evidence and /api/login. The browser
 * holds no Airtable credentials: creating a record needs the session token
 * from /api/login, and verification-code lookup is public.
 */

import { getSessionToken } from './uploadClient';

function reviveDates(evidence) {
  return {
    ...evidence,
    uploadDate: evidence.uploadDate ? new Date(evidence.uploadDate) : undefined,
    createdDate: evidence.createdDate ? new Date(evidence.createdDate) : new Date()
  };
}

async function callEvidenceApi(body, { authenticated = false } = {}) {
  const headers = { 'Content-Type': 'application/json' };
  if (authenticated) {
    const token = getSessionToken();
    if (!token) return { success: false, error: 'Your session has expired. Please log in again.' };
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch('/api/evidence', {
    method: 'POST',
    headers,
    body: JSON.stringify(body)
  });
  const result = await response.json().catch(() => ({}));

  if (!response.ok || !result.success) {
    return { success: false, error: result.error || `Request failed (${response.status})` };
  }
  return { success: true, data: reviveDates(result.data) };
}

class AirtableService {
  /**
   * Create an evidence record (requires a logged-in user with upload permission).
   * The server sets the record owner from the session, not from this payload.
   *
   * @param {Object} evidence - Evidence data
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  async createEvidenceRecord(evidence) {
    try {
      return await callEvidenceApi({ action: 'create', evidence }, { authenticated: true });
    } catch (error) {
      console.error('❌ Create evidence record failed:', error.message);
      return { success: false, error: error.message };
    }
  }

  /**
   * Look up an evidence record by verification code (public).
   *
   * @param {string} verificationCode - e.g. "ABC-DEF-GHI"
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  async getEvidenceByCode(verificationCode) {
    try {
      return await callEvidenceApi({ action: 'byCode', code: verificationCode });
    } catch (error) {
      console.error('❌ Get evidence by code failed:', error.message);
      return { success: false, error: error.message };
    }
  }
}

// Export singleton instance
const airtableService = new AirtableService();

export default airtableService;
export { AirtableService };
